import { Suspense, useEffect, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { LanguageWrapper } from '@/components/LanguageWrapper'
import { Layout } from '@/components/layout/Layout'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { ChatBot } from '@/components/ChatBot'
import { CookieConsent } from '@/components/CookieConsent'
import { ScrollToTop } from '@/components/ScrollToTop'
import { defaultLanguage } from '@/i18n'

// Pages
import { HomePage } from '@/pages/HomePage'
import { AboutPage } from '@/pages/AboutPage'
import { WhistleblowingPage } from '@/pages/WhistleblowingPage'
import { CathodicProtectionPage } from '@/pages/CathodicProtectionPage'
import { OxygenEvolutionPage } from '@/pages/OxygenEvolutionPage'
import { ElectrochlorinationPage } from '@/pages/ElectrochlorinationPage'
import { HiseochlorPage } from '@/pages/HiseochlorPage'
import { ChlorineAlkaliPage } from '@/pages/ChlorineAlkaliPage'
import { CertificationsPage } from '@/pages/CertificationsPage'
import { ContactPage } from '@/pages/ContactPage'
import { TermsPage } from '@/pages/TermsPage'
import { PrivacyPage } from '@/pages/PrivacyPage'
import { NotFoundPage } from '@/pages/NotFoundPage'

// True when running inside a headless prerender browser (Puppeteer sets this flag).
// Used to skip overlay UI (chatbot, cookie banner) from prerendered HTML snapshots.
const isPrerender =
  typeof navigator !== 'undefined' && navigator.webdriver === true

function PrerenderReadySentinel() {
  // Empty marker the prerender plugin waits for before snapshotting.
  // With translations bundled (see src/i18n/index.ts), the only thing we need
  // to wait for is one render cycle so Helmet has flushed meta tags to DOM.
  const [ready, setReady] = useState(false)

  useEffect(() => {
    const id = window.requestAnimationFrame(() => setReady(true))
    return () => window.cancelAnimationFrame(id)
  }, [])

  if (!ready) return null
  return <div data-prerender-ready style={{ display: 'none' }} aria-hidden />
}

function AppRoutes() {
  return (
    <Layout>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route index element={<HomePage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="whistleblowing" element={<WhistleblowingPage />} />
          <Route path="cathodic-protection" element={<CathodicProtectionPage />} />
          <Route path="oxygen-evolution" element={<OxygenEvolutionPage />} />
          <Route path="electrochlorination" element={<ElectrochlorinationPage />} />
          <Route path="hiseochlor" element={<HiseochlorPage />} />
          <Route path="chlorine-alkali" element={<ChlorineAlkaliPage />} />
          <Route path="certifications" element={<CertificationsPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <PrerenderReadySentinel />
      </Suspense>
    </Layout>
  )
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Redirect root to default language */}
        <Route path="/" element={<Navigate to={`/${defaultLanguage}`} replace />} />

        {/* Language-prefixed routes */}
        <Route path="/:lang/*" element={<LanguageWrapper />}>
          <Route path="*" element={<AppRoutes />} />
        </Route>
      </Routes>

      {!isPrerender && <ChatBot />}
      {!isPrerender && <CookieConsent />}
    </>
  )
}
